import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  closeDayDetail,
  fetchDayDetail,
  selectDayDetail,
  setDayDetailHorizon,
  setDayDetailTargetDay,
  selectHorizonOverviewData,
} from '../../../../../slices/reportsSlice';
import { RESOURCE_TIMEZONE } from '../../../../../utils/datetime';
import { SLIDE_OVER_BACKDROP_CLASS, SLIDE_OVER_PANEL_TRANSITION_CLASS, SLIDE_OVER_TOP_OFFSET_STYLE } from '../../../../../components/slideOverTransition';
import DayPenaltyBanner from './DayPenaltyBanner';
import SlotMatrixHeatmap from './SlotMatrixHeatmap';
import SlotBuildupStrip from './SlotBuildupStrip';
import DayAggregateFooter from './DayAggregateFooter';

const horizonLabel = (offset) => {
  if (offset == null) return '';
  if (offset === 0) return 'Intraday';
  if (offset === 1) return 'D+1';
  return `D+${offset}`;
};

const horizonLong = (offset) => {
  if (offset == null) return '';
  if (offset === 0) return 'Intraday';
  if (offset === 1) return 'Day-Ahead (D+1)';
  return `Extended (D+${offset})`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // For date-only `target_day` strings (parsed as UTC midnight by JS), force
  // the calendar day in UTC so the displayed weekday/day matches the slot.
  // For full timestamps the resource tz shifts the calendar correctly.
  const tz = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? 'UTC' : RESOURCE_TIMEZONE;
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: tz,
  }).format(d);
};

export default function DayDetailDrawer() {
  const dispatch = useDispatch();
  const detail = useSelector(selectDayDetail);
  const horizonOverviewData = useSelector(selectHorizonOverviewData);

  const availableHorizons = useMemo(() => {
    if (!Array.isArray(horizonOverviewData)) return [];
    return [...horizonOverviewData]
      .map((r) => r.horizon_offset)
      .filter((h) => typeof h === 'number')
      .sort((a, b) => a - b);
  }, [horizonOverviewData]);

  // Fetch when the drawer opens for a (resource, user, day, horizon) tuple.
  useEffect(() => {
    if (!detail.open) return;
    if (!detail.targetDay || !detail.resourceId) return;
    dispatch(fetchDayDetail({
      resourceId: detail.resourceId,
      userId: detail.userId,
      targetDay: detail.targetDay,
      horizonOffset: detail.horizonOffset,
    }));
  }, [
    dispatch,
    detail.open,
    detail.targetDay,
    detail.resourceId,
    detail.userId,
    detail.horizonOffset,
  ]);

  const scoreRow = useMemo(() => {
    if (!detail.scoreRows || detail.scoreRows.length === 0) return null;
    const matchHorizon = (r) => detail.horizonOffset == null || r.horizon_offset === detail.horizonOffset;
    const exact = detail.scoreRows.find(
      (r) => r.metric === 'rmse' && r.variable === 'q50' && matchHorizon(r)
    );
    if (exact) return exact;
    return detail.scoreRows.find(matchHorizon) || null;
  }, [detail.scoreRows, detail.horizonOffset]);

  const matrixRow = useMemo(() => {
    if (!detail.matrixRows || detail.matrixRows.length === 0) return null;
    return detail.matrixRows.find((r) => r.variable === 'q50') || detail.matrixRows[0];
  }, [detail.matrixRows]);

  // Build-up strip selection. A pinned row (click) wins over the transient
  // hover preview: once pinned, the strip stays on that row while you hover
  // others; clicking the pinned row again unpins it. `null` = nothing pinned,
  // so the strip follows the hovered row (defaulting to the first). Reset when
  // the underlying matrix changes (new day / horizon switch).
  const [selectedTs, setSelectedTs] = useState(null);
  const [previewTs, setPreviewTs] = useState(null);
  useEffect(() => {
    setSelectedTs(null);
    setPreviewTs(null);
  }, [matrixRow]);
  const handleSelectTs = (t) => setSelectedTs((prev) => (prev === t ? null : t));
  const activeTs = selectedTs != null ? selectedTs : (previewTs != null ? previewTs : 0);

  const handleClose = () => dispatch(closeDayDetail());
  const handleHorizonChange = (h) => {
    if (h === detail.horizonOffset) return;
    dispatch(setDayDetailHorizon(h));
  };
  // Normalize whatever shape `targetDay` arrives in (date-only string or full
  // ISO timestamp) into the `YYYY-MM-DD` value that `<input type="date">` needs.
  const datePickerValue = useMemo(() => {
    const t = detail.targetDay;
    if (!t) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10);
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  }, [detail.targetDay]);
  const handleDateChange = (e) => {
    const next = e.target.value;
    if (next) dispatch(setDayDetailTargetDay(next));
  };

  // Qualified happy path → tiny chip in the header. Penalty / skipped → big
  // banner in the body so the user can't miss it.
  const isQualified = scoreRow && !scoreRow.is_penalty && scoreRow.value != null;

  // Keep the Dialog mounted while closed: unmounting it would skip the enter
  // transition and drop the leave transition (see slideOverTransition.js).
  return (
    <Dialog open={detail.open} onClose={handleClose} transition className="relative z-50">
      <DialogBackdrop transition className={SLIDE_OVER_BACKDROP_CLASS} />
      <div className="fixed inset-0 z-10 flex justify-end pointer-events-none" style={SLIDE_OVER_TOP_OFFSET_STYLE}>
        <DialogPanel
          transition
          className={`pointer-events-auto h-full w-full max-w-4xl bg-surface dark:bg-gray-870 shadow-xl border-l border-gray-200/70 dark:border-gray-600/70 flex flex-col ${SLIDE_OVER_PANEL_TRANSITION_CLASS}`}
        >
          <header className="flex-shrink-0 border-b border-gray-200/70 dark:border-gray-600/70 px-5 sm:px-14 py-4 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Per-day scoring drill-down
                </div>
                <div className="mt-0.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {formatDate(detail.targetDay)}
                  </span>
                  <span
                    className="text-xs text-gray-500 dark:text-gray-400"
                    title="Slot matrix is persisted for the deterministic q50 forecast only — probabilistic (q10/q90) drill-down deferred until those matrices are persisted."
                  >
                    Deterministic · RMSE · q50
                  </span>
                  {isQualified ? (
                    <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Qualified</span>
                  ) : scoreRow?.is_penalty ? (
                    <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Penalty</span>
                  ) : null}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <label className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                  <span className="sr-only">Pick day</span>
                  <input
                    type="date"
                    value={datePickerValue}
                    onChange={handleDateChange}
                    disabled={detail.loading}
                    className="rounded-md border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-60"
                    title="Jump to another day"
                    aria-label="Pick day"
                  />
                </label>
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-md p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                  aria-label="Close drawer"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Horizon switcher: only render when we have something to switch to. */}
            {availableHorizons.length > 1 && detail.horizonOffset != null && (
              <div className="flex items-center gap-1.5 text-[11px]">
                <span className="text-gray-500 dark:text-gray-400">Horizon:</span>
                <div className="inline-flex rounded-md border border-gray-200/70 dark:border-gray-600/70 overflow-hidden">
                  {availableHorizons.map((h) => {
                    const active = h === detail.horizonOffset;
                    return (
                      <button
                        key={h}
                        type="button"
                        onClick={() => handleHorizonChange(h)}
                        className={`px-2 py-0.5 transition-colors ${
                          active
                            ? 'bg-brand-500 text-white'
                            : 'bg-surface dark:bg-gray-870 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                        title={horizonLong(h)}
                      >
                        {horizonLabel(h)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </header>

          {/* Generous side padding gives a safe gutter to scroll the page even
              while the pointer is near the matrix's internal scroll area. */}
          <div className="flex-1 overflow-y-auto px-5 sm:px-14 py-4 space-y-4">
            {detail.loading ? (
              <DrawerSkeleton />
            ) : detail.error ? (
              <ErrorState error={detail.error} />
            ) : (
              <>
                <DayPenaltyBanner scoreRow={scoreRow} horizonOffset={detail.horizonOffset} />
                <DayAggregateFooter
                  matrixJson={matrixRow?.matrix_json}
                  scoreRow={scoreRow}
                  horizonOffset={detail.horizonOffset}
                />
                <SlotMatrixHeatmap
                  matrixJson={matrixRow?.matrix_json}
                  horizonOffset={detail.horizonOffset}
                  selectedIndex={selectedTs}
                  onSelect={handleSelectTs}
                  onPreview={setPreviewTs}
                />
                <SlotBuildupStrip
                  matrixJson={matrixRow?.matrix_json}
                  timestampIndex={activeTs}
                  horizonOffset={detail.horizonOffset}
                  pinned={selectedTs != null}
                />
              </>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

function DrawerSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      <div className="h-14 rounded-md border border-gray-300 dark:border-gray-600" />
      <div className="h-72 rounded-md border border-gray-300 dark:border-gray-600" />
      <div className="h-28 rounded-md border border-gray-300 dark:border-gray-600" />
    </div>
  );
}

function ErrorState({ error }) {
  const msg = typeof error === 'string' ? error : (error?.message || JSON.stringify(error));
  return (
    <div className="rounded-md border border-red-300 dark:border-red-700/60 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-900 dark:text-red-200">
      Failed to load day detail: {msg}
    </div>
  );
}

